import { Input, Select, Space } from "antd";
import type { Rule } from "antd/es/form";
import { useEffect, useRef, useState } from "react";
import {
  AsYouType,
  CountryCode,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";

interface CountryOption {
  value: CountryCode;
  name: string;
  dial: string;
}

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

/** Every country libphonenumber knows, with its English name + calling code, sorted by name. */
const COUNTRY_OPTIONS: CountryOption[] = getCountries()
  .map((code) => ({
    value: code,
    name: regionNames.of(code) ?? code,
    dial: getCountryCallingCode(code),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

type FlagSvgs = Record<string, string>;

/** Flag SVGs (country-flag-icons, same ISO codes as libphonenumber) are ~1MB raw, so they load as
 *  one lazy chunk on first use and are shared by every PhoneInput — never in the main bundle. */
let flagsPromise: Promise<FlagSvgs> | undefined;
const loadFlags = (): Promise<FlagSvgs> =>
  (flagsPromise ??= import("country-flag-icons/string/3x2").then(
    (m) => m as unknown as FlagSvgs,
  ));

function Flag(props: { svg?: string }): JSX.Element {
  return props.svg ? (
    <img
      src={`data:image/svg+xml;utf8,${encodeURIComponent(props.svg)}`}
      alt=""
      className="inline-block h-3 w-[18px] shrink-0 rounded-[2px] object-cover shadow-[0_0_0_1px_rgba(0,0,0,0.1)]"
    />
  ) : (
    <span className="inline-block h-3 w-[18px] shrink-0" />
  );
}

const digitsOnly = (text: string): string => text.replace(/\D/g, "");

/** Splits a stored value into country + national number. E.164 values (`+30...`) resolve their
 *  country; legacy values without a `+` stay as-is with no country selected. */
function splitValue(value?: string): {
  country?: CountryCode;
  national: string;
} {
  if (!value) return { national: "" };
  const parsed = value.startsWith("+")
    ? parsePhoneNumberFromString(value)
    : undefined;
  return parsed?.country
    ? { country: parsed.country, national: parsed.nationalNumber }
    : { national: value };
}

/** Joins country + national number into E.164 (`GR` + `6955444617` -> `+306955444617`). Falls
 *  back to plain concatenation when libphonenumber can't parse the (still incomplete) number. */
function joinValue(country: CountryCode | undefined, national: string): string {
  const digits = digitsOnly(national);
  if (!digits) return "";
  if (!country) return national;
  const parsed = parsePhoneNumberFromString(digits, country);
  // Forgive a habitual leading trunk "0" in countries that don't use one (GR `06955…`).
  if (!parsed?.isValid() && digits.startsWith("0")) {
    const stripped = parsePhoneNumberFromString(digits.slice(1), country);
    if (stripped?.isValid()) return stripped.number;
  }
  return parsed?.number ?? `+${getCountryCallingCode(country)}${digits}`;
}

/** Human display of a stored phone value: `+306955444617` -> `+30 695 544 4617`. */
export function formatPhone(value?: string): string | undefined {
  if (!value) return value;
  return value.startsWith("+") ? new AsYouType().input(value) : value;
}

/** Form rule: E.164 values must be a valid number for their country; legacy values pass. */
export const phoneRule: Rule = {
  validator: (_, value?: string) =>
    !value || !value.startsWith("+") || isValidPhoneNumber(value)
      ? Promise.resolve()
      : Promise.reject(
          new Error("Invalid phone number for the selected country"),
        ),
};

/**
 * Phone input with a searchable country-code picker (search by English country name, ISO code or
 * dial code). Binds to an antd Form.Item via value/onChange; the value is an E.164 string.
 */
export default function PhoneInput(props: {
  value?: string;
  onChange?: (value: string) => void;
  size?: "small" | "middle" | "large";
  defaultCountry?: CountryCode;
}): JSX.Element {
  const { value, onChange, size, defaultCountry } = props;
  const initial = splitValue(value);
  const [country, setCountry] = useState<CountryCode | undefined>(
    initial.country ?? (value ? undefined : defaultCountry),
  );
  const [national, setNational] = useState(initial.national);
  const lastEmitted = useRef(value);
  const [flags, setFlags] = useState<FlagSvgs>({});

  useEffect(() => {
    let active = true;
    loadFlags()
      .then((loaded) => active && setFlags(loaded))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // Re-sync when the form value changes from outside (reset / load), not from our own emits.
  useEffect(() => {
    if (value === lastEmitted.current) return;
    const next = splitValue(value);
    setCountry(next.country ?? (value ? undefined : defaultCountry));
    setNational(next.national);
    lastEmitted.current = value;
  }, [value, defaultCountry]);

  function emit(
    nextCountry: CountryCode | undefined,
    nextNational: string,
  ): void {
    const joined = joinValue(nextCountry, nextNational);
    lastEmitted.current = joined;
    onChange?.(joined);
  }

  function onCountryChange(next: CountryCode): void {
    setCountry(next);
    emit(next, national);
  }

  function onNationalChange(text: string): void {
    // A pasted international number (`+30...`) picks its own country.
    const parsed = text.startsWith("+")
      ? parsePhoneNumberFromString(text)
      : undefined;
    if (parsed?.country) {
      setCountry(parsed.country);
      setNational(parsed.nationalNumber);
      emit(parsed.country, parsed.nationalNumber);
      return;
    }
    setNational(text);
    emit(country, text);
  }

  // The field's Form.Item is `noStyle` (DescriptionsFields), which hides rule messages — so the
  // control explains its own red state instead of leaving Save to fail silently.
  const invalid =
    !!value && value.startsWith("+") && !isValidPhoneNumber(value);
  const countryName = COUNTRY_OPTIONS.find((o) => o.value === country)?.name;

  return (
    <div className="w-full">
      <Space.Compact className="w-full">
        <Select<CountryCode>
          size={size}
          status={invalid ? "error" : undefined}
          showSearch
          value={country}
          placeholder="Code"
          onChange={onCountryChange}
          style={{ width: 120, flexShrink: 0 }}
          popupMatchSelectWidth={300}
          optionLabelProp="short"
          filterOption={(input, option) => {
            const q = input.trim().toLowerCase().replace(/^\+/, "");
            const o = option as unknown as CountryOption | undefined;
            return (
              !!o &&
              (o.name.toLowerCase().includes(q) ||
                o.value.toLowerCase() === q ||
                o.dial.startsWith(q))
            );
          }}
          options={COUNTRY_OPTIONS.map((o) => ({
            ...o,
            short: (
              <span className="flex items-center gap-2">
                <Flag svg={flags[o.value]} />
                <span>+{o.dial}</span>
              </span>
            ),
            label: (
              <div className="flex items-center gap-2">
                <Flag svg={flags[o.value]} />
                <span className="flex-1 truncate">{o.name}</span>
                <span className="text-gray-400">+{o.dial}</span>
              </div>
            ),
          }))}
        />
        <Input
          size={size}
          status={invalid ? "error" : undefined}
          value={national}
          inputMode="tel"
          placeholder="Phone number"
          onChange={(e) => onNationalChange(e.target.value)}
        />
      </Space.Compact>
      {invalid && (
        <div className="mt-1 text-xs text-red-500">
          Not a valid {countryName ?? "phone"} number
        </div>
      )}
    </div>
  );
}
